import React, { useEffect, useMemo, useState } from "react";
import { BaseModal } from "@src/common/components";
import { useShipmentSelectionContext } from "../../context/ShipmentSelectionContext";
import {
  BulkShipmentAction,
  useBulkShipmentActions,
} from "../../hooks/useBulkShipmentActions";
import { useActionPaletteKeyboard } from "../ShipmentRowActionPalette/hooks/useActionPaletteKeyboard";
import { PaletteSearchInput } from "../ShipmentRowActionPalette/components/PaletteSearchInput";
import { PaletteActionList } from "../ShipmentRowActionPalette/components/PaletteActionList";

// Spark-style command palette for bulk actions: opens on right-clicking a row
// while shipments are checked, and runs an action against the whole selection.
// Shares its action definitions with the button bar via useBulkShipmentActions.
export function BulkActionPalette() {
  const {
    checkedShipments,
    isBulkActionPaletteOpen,
    setIsBulkActionPaletteOpen,
  } = useShipmentSelectionContext();
  const { actions } = useBulkShipmentActions();

  const isOpen = isBulkActionPaletteOpen && checkedShipments.length > 0;
  const close = () => setIsBulkActionPaletteOpen(false);

  const [query, setQuery] = useState("");

  const runnableActions = useMemo(
    () => actions.filter((action) => action.show && !action.disabled),
    [actions]
  );

  const filteredActions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return runnableActions;
    return runnableActions.filter((action) =>
      action.label.toLowerCase().includes(term)
    );
  }, [runnableActions, query]);

  useEffect(() => {
    setQuery("");
  }, [isBulkActionPaletteOpen]);

  const runAction = (action: BulkShipmentAction) => {
    close();
    action.onClick();
  };

  const { highlightedIndex, setHighlightedIndex, itemRefs, handleKeyDown } =
    useActionPaletteKeyboard({
      itemCount: filteredActions.length,
      isOpen: isBulkActionPaletteOpen,
      resetKey: checkedShipments.length,
      onEnter: (index) => {
        const action = filteredActions[index];
        if (action) runAction(action);
      },
      onClose: close,
    });

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={close}
      overrides={{
        Root: {
          style: {
            zIndex: 2000,
          },
        },
        Dialog: {
          style: {
            width: "480px",
            maxWidth: "90vw",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
          },
          props: {
            className: "dark:bg-gray-800",
          },
        },
      }}
    >
      <div onKeyDown={handleKeyDown} className="dark:bg-gray-800">
        <div className="px-4 py-3 border-b dark:border-gray-700">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {checkedShipments.length} order
            {checkedShipments.length === 1 ? "" : "s"} selected
          </span>
        </div>
        <PaletteSearchInput query={query} setQuery={setQuery} />
        <PaletteActionList
          actions={filteredActions}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          itemRefs={itemRefs}
          onSelect={runAction}
          getShortcut={(key) =>
            runnableActions
              .find((action) => action.key === key)
              ?.shortcut?.toUpperCase()
          }
          isDestructive={(action) => action.color === "red"}
        />
      </div>
    </BaseModal>
  );
}

export default BulkActionPalette;
